import type { ColumnDef } from '@tanstack/react-table';

import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDynamicLocale } from '@/hoslog-intl';
import { EditIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { DeleteGalleryPayload, GalleryManagement } from '@/services/galleryManagement/galleryManagement.types';
import { Trash2 } from 'lucide-react';
import { ReactNode } from 'react';
import { AddHostGalleryForm } from '../AddHostGalleryForm';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => void | Promise<void>;
  onCancel: () => void;
}) => void;

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

export function getColumns(
  mutate: (payload: DeleteGalleryPayload) => Promise<unknown>,
  openConfirmation: OpenConfirmationProps,
  onOpen: OnOpenProps,
  t: ReturnType<typeof useDynamicLocale>['t'],
): ColumnDef<GalleryManagement>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => (
        <DataTableColumnHeader title={t('storeManagement.galleryManagement.page.table.sn')} column={column} />
      ),
      cell: ({ row, table }) => (
        <div className="font-medium text-caption truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'imageTitle',
      header: ({ column }) => (
        <DataTableColumnHeader title={t('storeManagement.galleryManagement.page.table.imageTitle')} column={column} />
      ),
      cell: ({ row }) => {
        const imageTitle = row.original.gallery.description;
        return <div className="font-medium text-caption truncate max-w-[20rem]">{imageTitle}</div>;
      },
      enableHiding: false,
      enableSorting: false,
    },
    {
      accessorKey: 'image',
      header: ({ column }) => (
        <DataTableColumnHeader title={t('storeManagement.galleryManagement.page.table.image')} column={column} />
      ),
      cell: ({ row }) => {
        const image = row.original.gallery;
        const imgAlt = image?.alt || '';

        const imgPath = image?.path || '';
        return (
          <div>
            <img src={imgPath} alt={imgAlt} className="h-15 aspect-[16 / 9] rounded object-cover" />
          </div>
        );
      },
      enableHiding: false,
      enableSorting: false,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader title={t('storeManagement.galleryManagement.page.table.updatedOn')} column={column} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium text-caption whitespace-nowrap">
            {row.getValue('updatedDate') ? formatDate(Number(row.getValue('updatedDate')), 'YYYY/MM/DD HH:mm:ss') : '-'}
          </div>
        );
      },

      enableHiding: false,
    },
    {
      header: ({ column }) => (
        <DataTableColumnHeader title={t('storeManagement.galleryManagement.page.table.action')} column={column} />
      ),
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <EditIcon
            className="text-foreground-subtle size-4"
            onClick={() =>
              onOpen({
                modalTitle: 'storeManagement.galleryManagement.page.addGallery.addImage',
                title: 'Manage HostNameGallery',
                okText: t('storeManagement.basicStores.galleryManagement.button.submit'),
                size: 'lg',
                component: (
                  <AddHostGalleryForm
                    clubId={row.original.clubId}
                    hostId={row.original.hostId}
                    galleryId={row.original.galleryId}
                    defaultValues={{
                      imageTitle: row.original.gallery.description,
                      imageSrc: row.original.gallery.path,
                    }}
                  />
                ),
              })
            }
          />
          <Trash2
            size={16}
            onClick={() => {
              openConfirmation({
                title: t('common.page.modal.deleteGallery'),
                description: t('storeManagement.basicStores.confirmationModal.deleteItem'),
                cancelLabel: t('common.page.modal.no'),
                actionLabel: t('common.page.modal.submit'),
                onAction: async () => {
                  await mutate({
                    galleryId: row.original.galleryId || '',
                    clubId: row.original.clubId || '',
                  });
                },
                onCancel: () => {},
                size: 'lg',
                variant: 'destructive',
              });
            }}
            className="cursor-pointer text-destructive-text hover:text-destructive-text ml-2"
          />
        </div>
      ),
      size: 40,
    },
  ];
}
